import { CHALLENGE_GAMES, type ChallengeGame, type OnlinePlayer } from '@xox/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useFetch } from '../../hooks/useFetch'
import { api } from '../../lib/api'
import { GAME_BY_CHALLENGE, ROOM_PATH_FOR } from '../../lib/games'
import { useSession } from '../../store/session'
import { Avatar } from '../Avatar'
import { GameLogo } from '../art/GameLogo'

/**
 * Who has the arena open right now, with their level and one challenge button per game. Mount
 * it when it becomes visible: it loads once per mount.
 */
export function OnlineList() {
  const me = useSession((s) => s.me)
  const navigate = useNavigate()
  const { data, error: loadError } = useFetch(api.online)
  const [sending, setSending] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const hidden = me?.appearOffline ?? false

  const challenge = async (p: OnlinePlayer, game: ChallengeGame) => {
    setSending(p.id)
    setError(null)
    try {
      const { code } = await api.challenge(p.id, game)
      navigate(`${ROOM_PATH_FOR[game]}/${code}`)
    } catch (err) {
      setError((err as Error).message)
      setSending(null)
    }
  }

  if (loadError) return <p className="text-sm text-rose-600">{loadError}</p>
  if (!data) return <p className="text-sm text-slate-500">Looking around…</p>
  const others = data.players.filter((p) => p.id !== me?.playerId)
  return (
    <div className="flex flex-col gap-2">
      {others.length === 0 && (
        <p className="text-sm text-slate-500">Nobody else is here right now.</p>
      )}
      <ul className="flex flex-col gap-1">
        {data.players.map((p) => {
          const you = p.id === me?.playerId
          return (
            <li key={p.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
              <Avatar id={p.id} avatar={p.avatar} color={p.color} size="md" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">
                  {p.nickname}
                  {you && <span className="text-slate-500"> · you</span>}
                </div>
                <div className="text-xs text-slate-500">Level {p.level}</div>
              </div>
              {!you && !hidden && (
                <div
                  role="group"
                  className="flex shrink-0 gap-1"
                  aria-label={`Challenge ${p.nickname}`}
                >
                  {CHALLENGE_GAMES.map((game) => (
                    <button
                      key={game}
                      type="button"
                      disabled={sending !== null}
                      onClick={() => void challenge(p, game)}
                      title={`Challenge to ${GAME_BY_CHALLENGE[game].title}`}
                      aria-label={`Challenge ${p.nickname} to ${GAME_BY_CHALLENGE[game].title}`}
                      className="rounded-xl bg-slate-100 p-1 transition-transform hover:scale-110 disabled:opacity-50 dark:bg-slate-800"
                    >
                      <GameLogo game={GAME_BY_CHALLENGE[game].to} className="size-8" compact />
                    </button>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {hidden && others.length > 0 && (
        <p className="text-xs text-slate-500">
          You appear offline, so challenges are off. Turn it off on your profile to play.
        </p>
      )}
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </div>
  )
}
